import type {
    AddToCartInput,
    RemoveFromCartInput,
    SelectItemInput,
    SelectModifyServiceInput,
    ViewItemInput,
    ViewItemListInput,
} from '../eventInputs';
import { mapHudsonCheckoutToTrackingCommerce, mapHudsonCheckoutUser } from '../sources/checkout/hudsonCheckout';
import { mapHybrisCheckoutToTrackingCommerce } from '../sources/checkout/hybrisCheckout';
import type { CheckoutInput } from '../sources/checkout/types';
import { HUDSON_CHECKOUT_EVENTS, HYBRIS_CHECKOUT_EVENTS } from '../sources/checkout/types';
import { createPublicOfferGAEvent } from '../sources/offers/publicOffer';
import { mapPublicServicePlansToTrackingCommerce } from '../sources/plans/publicServicePlans';
import {
    mapFixPlansToTrackingCommerce,
    mapRatePlansToTrackingCommerce,
    mapServicePlanEventToTrackingCommerce,
} from '../sources/plans/servicePlans';
import type {
    FixPlanSource,
    PlanEventInput,
    PublicServicePlanEventInput,
    RatePlanEventInput,
    RatePlanSource,
    RatePlanType,
    ServicePlanEventInput,
} from '../sources/plans/types';
import {
    mapProductInsuranceToTrackingCommerce,
    mapProductPageCartToAddToCartTrackingCommerce,
    mapProductPageCartToViewItemTrackingCommerce,
    mapProductUpsellToAddToCartTrackingCommerce,
    mapProductUpsellToItemTrackingCommerce,
    mapProductUpsellToRemoveFromCartTrackingCommerce,
} from '../sources/productPage/productPage';
import { mapSearchProductsToTrackingCommerce } from '../sources/search/search';
import { mapOTTPlansToTrackingCommerce, mapTVOrderingToTrackingCommerce } from '../sources/tv/tv';
import { OTT_CHECKOUT_EVENTS, TV_ORDERING_CHECKOUT_EVENTS } from '../sources/tv/types';
import type { GTMEcommerceEvent, GTMEventName, TrackingCommerce } from '../types';
import { GTMItemServiceType } from '../types';
import { createGTMEcommerceEvent } from './ecommerceMapper';
import type { GTMUserDataOptions, GTMUserDataSource } from './user';
import { GTMUserType, mapGTMUserData } from './user';

const DEFAULT_USER_OPTIONS: GTMUserDataOptions = { guestUserType: GTMUserType.Weak };
const OPTIONAL_USER_OPTIONS: GTMUserDataOptions = { guestUserType: '' };
const RATE_PLAN_TYPES = new Set<RatePlanType>(['mhs', 'mbb']);

const createUserEcommerceGAEvent = (
    event: GTMEventName,
    commerce: TrackingCommerce,
    userData?: GTMUserDataSource | null,
    userOptions: GTMUserDataOptions = DEFAULT_USER_OPTIONS,
): GTMEcommerceEvent => {
    const user = mapGTMUserData(userData, userOptions);

    return createGTMEcommerceEvent(event, commerce, {
        user_id: user.user_id,
        user_type: user.user_type,
        email: user.email,
        phone: user.phone,
    });
};

export const createAddToCartGAEvent = (event: GTMEventName, input: AddToCartInput): GTMEcommerceEvent => {
    switch (input.source) {
        case 'productPage':
            return createGTMEcommerceEvent(event, mapProductPageCartToAddToCartTrackingCommerce(input.data.cart));
        case 'productUpsell':
            return createGTMEcommerceEvent(event, mapProductUpsellToAddToCartTrackingCommerce(input.data));
        case 'productInsurance':
            return createGTMEcommerceEvent(event, mapProductInsuranceToTrackingCommerce(input.data.cart));
        case 'ottPlans':
            return createUserEcommerceGAEvent(
                event,
                mapOTTPlansToTrackingCommerce(input.data),
                input.data.userData,
                OPTIONAL_USER_OPTIONS,
            );
        case 'tvOrdering':
            return createUserEcommerceGAEvent(
                event,
                mapTVOrderingToTrackingCommerce(input.data),
                input.data.userData,
                OPTIONAL_USER_OPTIONS,
            );
        default:
            throw new Error('Unsupported add_to_cart source');
    }
};

export const createRemoveFromCartGAEvent = (event: GTMEventName, input: RemoveFromCartInput): GTMEcommerceEvent => {
    switch (input.source) {
        case 'productUpsell':
            return createGTMEcommerceEvent(
                event,
                mapProductUpsellToRemoveFromCartTrackingCommerce(input.data.cart, input.data.entry),
            );
        case 'productInsurance':
            return createGTMEcommerceEvent(event, mapProductInsuranceToTrackingCommerce(input.data.cart));
        default:
            throw new Error('Unsupported remove_from_cart source');
    }
};

export const createViewItemGAEvent = (event: GTMEventName, input: ViewItemInput): GTMEcommerceEvent | undefined => {
    switch (input.source) {
        case 'productUpsell':
            return createGTMEcommerceEvent(event, mapProductUpsellToItemTrackingCommerce(input.data));
        case 'productPage': {
            const ecommerce = mapProductPageCartToViewItemTrackingCommerce(input.data.cart);

            return ecommerce ? createGTMEcommerceEvent(event, ecommerce) : undefined;
        }
        default:
            throw new Error('Unsupported view_item source');
    }
};

const createRatePlanGAEvent = (event: GTMEventName, input: RatePlanEventInput): GTMEcommerceEvent => {
    return createUserEcommerceGAEvent(event, mapRatePlansToTrackingCommerce(input.data), input.data.userData);
};

const createServicePlanGAEvent = (event: GTMEventName, input: ServicePlanEventInput): GTMEcommerceEvent =>
    createUserEcommerceGAEvent(event, mapServicePlanEventToTrackingCommerce(input.data), input.data.userData);

const createPlanGAEvent = (event: GTMEventName, input: PlanEventInput): GTMEcommerceEvent => {
    const { data } = input;

    if (RATE_PLAN_TYPES.has(data.planType as RatePlanType)) {
        return createRatePlanGAEvent(event, {
            source: 'ratePlans',
            data: {
                ...data,
                planType: data.planType as RatePlanType,
                plans: data.plans as RatePlanSource[],
            },
        });
    }

    if (event === 'select_item' && data.planType === 'fix') {
        return createUserEcommerceGAEvent(
            event,
            mapFixPlansToTrackingCommerce({
                commitmentTypeId: data.commitmentTypeId,
                plans: data.plans as FixPlanSource[],
                segment: data.segment,
            }),
            data.userData,
        );
    }

    return createServicePlanGAEvent(event, { source: 'servicePlans', data });
};

const createPublicServicePlanGAEvent = (
    event: GTMEventName,
    input: PublicServicePlanEventInput,
    serviceType: GTMItemServiceType,
): GTMEcommerceEvent =>
    createUserEcommerceGAEvent(
        event,
        mapPublicServicePlansToTrackingCommerce(input.data, serviceType),
        input.data.userData,
        OPTIONAL_USER_OPTIONS,
    );

export const createViewItemListGAEvent = (event: GTMEventName, input: ViewItemListInput): GTMEcommerceEvent => {
    switch (input.source) {
        case 'plans':
            return createPlanGAEvent(event, input);
        case 'ratePlans':
            return createRatePlanGAEvent(event, input);
        case 'servicePlans':
            return createServicePlanGAEvent(event, input);
        case 'publicServicePlans':
            return createPublicServicePlanGAEvent(event, input, GTMItemServiceType.NewSale);
        case 'ottPlans':
            return createUserEcommerceGAEvent(
                event,
                mapOTTPlansToTrackingCommerce(input.data),
                input.data.userData,
                OPTIONAL_USER_OPTIONS,
            );
        case 'searchProducts':
            return createGTMEcommerceEvent(event, mapSearchProductsToTrackingCommerce(input.data));
        default:
            throw new Error('Unsupported view_item_list source');
    }
};

export const createSelectItemGAEvent = (event: GTMEventName, input: SelectItemInput): GTMEcommerceEvent => {
    switch (input.source) {
        case 'plans':
            return createPlanGAEvent(event, input);
        case 'ratePlans':
            return createRatePlanGAEvent(event, input);
        case 'fixPlans':
            return createUserEcommerceGAEvent(event, mapFixPlansToTrackingCommerce(input.data), input.data.userData);
        case 'servicePlans':
            return createServicePlanGAEvent(event, input);
        case 'publicServicePlans':
            return createPublicServicePlanGAEvent(event, input, GTMItemServiceType.Modification);
        case 'ottPlans':
            return createUserEcommerceGAEvent(
                event,
                mapOTTPlansToTrackingCommerce(input.data),
                input.data.userData,
                OPTIONAL_USER_OPTIONS,
            );
        case 'searchProducts':
            return createGTMEcommerceEvent(event, mapSearchProductsToTrackingCommerce(input.data));
        case 'publicOffers':
            return createPublicOfferGAEvent(event, input.data);
        case 'productUpsell':
            return createGTMEcommerceEvent(event, mapProductUpsellToItemTrackingCommerce(input.data));
        default:
            throw new Error('Unsupported select_item source');
    }
};

export const createSelectModifyServiceGAEvent = (
    event: GTMEventName,
    input: SelectModifyServiceInput,
): GTMEcommerceEvent => {
    switch (input.source) {
        case 'plans':
            return createPlanGAEvent(event, input);
        case 'ratePlans':
            return createRatePlanGAEvent(event, input);
        case 'servicePlans':
            return createServicePlanGAEvent(event, input);
        case 'publicServicePlans':
            return createPublicServicePlanGAEvent(event, input, GTMItemServiceType.Modification);
        default:
            throw new Error('Unsupported select_modify_service source');
    }
};

export const createCheckoutGAEvent = (input: CheckoutInput): GTMEcommerceEvent => {
    switch (input.source) {
        case 'hudsonCheckout': {
            const user = mapHudsonCheckoutUser(input.data);

            return createGTMEcommerceEvent(
                HUDSON_CHECKOUT_EVENTS[input.data.action],
                mapHudsonCheckoutToTrackingCommerce(input.data),
                {
                    user_id: user.user_id,
                    user_type: user.user_type,
                    email: user.email,
                    phone: user.phone,
                },
            );
        }
        case 'hybrisCheckout':
            return createGTMEcommerceEvent(
                HYBRIS_CHECKOUT_EVENTS[input.data.action],
                mapHybrisCheckoutToTrackingCommerce(input.data),
            );
        case 'ottPlans':
            return createUserEcommerceGAEvent(
                OTT_CHECKOUT_EVENTS[input.data.action],
                mapOTTPlansToTrackingCommerce(input.data),
                input.data.userData,
                OPTIONAL_USER_OPTIONS,
            );
        case 'tvOrdering':
            return createUserEcommerceGAEvent(
                TV_ORDERING_CHECKOUT_EVENTS[input.data.action],
                mapTVOrderingToTrackingCommerce(input.data),
                input.data.userData,
                OPTIONAL_USER_OPTIONS,
            );
        default:
            throw new Error('Unsupported checkout source');
    }
};
